import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSession } from '../session';

/** The sizes offered as buttons, as a share of the column the image sits in. */
const presets = [25, 50, 75, 100];

/** Smallest share a drag can make an image, so it cannot vanish. */
const smallest = 10;

interface Frame { left: number; top: number; width: number; height: number }

/** The frame drawn over the image selected in an editor: a handle in the
 *  corner to drag it to a size, and buttons for the usual ones.
 *
 *  It sits beside the editable box, never inside it. Anything put inside
 *  would end up in the saved body, and Chrome's undo cannot follow a change
 *  it did not make; the editor applies the chosen size itself, as one step
 *  the browser can take back.
 *
 *  Sizes are a percentage of the column, not pixels: the box being written in
 *  and the page the body is read on are not the same width, and a phone is
 *  narrower than both. */
export function ImageSizer({
  img, wrap, box, onResize,
}: {
  img: HTMLImageElement;
  /** What the frame is positioned against. */
  wrap: HTMLElement;
  /** The editable box, which scrolls on its own. */
  box: HTMLElement;
  onResize: (img: HTMLImageElement, percent: number) => void;
}) {
  const { t } = useSession();
  const [frame, setFrame] = useState<Frame | null>(null);
  // The width the image would have while the handle is held, in pixels.
  const [ghost, setGhost] = useState<number | null>(null);
  const drag = useRef<{ x: number; width: number; column: number } | null>(null);

  const place = () => {
    const w = wrap.getBoundingClientRect();
    const r = img.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    // Scrolled out of the box: nothing to hold on to.
    const next = r.bottom < b.top || r.top > b.bottom
      ? null
      : { left: r.left - w.left, top: r.top - w.top, width: r.width, height: r.height };
    // Only when it moved: this runs after every render, and a new object
    // every time would render again for ever.
    setFrame((prev) => (same(prev, next) ? prev : next));
  };

  useLayoutEffect(place); // after every render of the editor, which is when things move
  useEffect(() => {
    box.addEventListener('scroll', place);
    window.addEventListener('resize', place);
    img.addEventListener('load', place);
    return () => {
      box.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
      img.removeEventListener('load', place);
    };
  });

  if (!frame) return null;

  const column = columnWidth(img);
  const current = currentPercent(img, frame.width, column);
  const shown = ghost ?? frame.width;
  const showing = ghost === null ? current : toPercent(ghost, column);

  return (
    <div
      className="rich-frame"
      style={{
        left: frame.left, top: frame.top, width: shown,
        height: frame.width ? frame.height * (shown / frame.width) : frame.height,
      }}
    >
      <div className="rich-sizes" role="toolbar" aria-label={t.imageSize}>
        {presets.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={current === p}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onResize(img, p)}
          >
            {p}%
          </button>
        ))}
        {ghost !== null && <span className="rich-size-now">{showing}%</span>}
      </div>
      <span
        className="rich-handle"
        role="slider"
        aria-label={t.imageSize}
        aria-valuemin={smallest}
        aria-valuemax={100}
        aria-valuenow={current}
        tabIndex={-1}
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, width: frame.width, column };
          setGhost(frame.width);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          const min = (d.column * smallest) / 100;
          setGhost(Math.min(d.column, Math.max(min, d.width + e.clientX - d.x)));
        }}
        onPointerUp={() => {
          const d = drag.current;
          drag.current = null;
          if (d && ghost !== null) onResize(img, toPercent(ghost, d.column));
          setGhost(null);
        }}
        onPointerCancel={() => { drag.current = null; setGhost(null); }}
      />
    </div>
  );
}

function same(a: Frame | null, b: Frame | null): boolean {
  if (!a || !b) return a === b;
  return Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5
    && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
}

/** Rounded to fives: nobody means 47%, and a size that is a round number is
 *  one the buttons can show as chosen. */
function toPercent(width: number, column: number): number {
  if (!column) return 100;
  const p = Math.round((width / column) * 20) * 5;
  return Math.min(100, Math.max(smallest, p));
}

function currentPercent(img: HTMLImageElement, width: number, column: number): number {
  const set = /^(\d+(?:\.\d+)?)%$/.exec(img.getAttribute('width') ?? '');
  return set ? Number(set[1]) : toPercent(width, column);
}

/** The width a percentage on the image is a share of: the content box of the
 *  block it sits in, which inside a list item is narrower than the editor. */
function columnWidth(img: HTMLImageElement): number {
  const parent = img.parentElement;
  if (!parent) return 0;
  const s = getComputedStyle(parent);
  return parent.clientWidth - parseFloat(s.paddingLeft) - parseFloat(s.paddingRight);
}
